/**
 * useTableSort Hook
 * 테이블 정렬 로직을 재사용하기 위한 커스텀 훅
 */
import { useState, useMemo, useCallback } from 'react';

// ========================================
// Types
// ========================================
export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
    columnKey: string | null;
    direction: SortDirection;
}

export interface ColumnSortConfig {
    columnIndex: number | null;
    direction: SortDirection;
}

interface UseTableSortOptions<T> {
    /** 정렬할 데이터 배열 */
    data: T[];
    /** 숫자 타입으로 처리할 컬럼 키 목록 */
    numericColumns?: (keyof T)[];
    /** 날짜 타입으로 처리할 컬럼 키 목록 */
    dateColumns?: (keyof T)[];
    /** 커스텀 정렬 순서를 지정할 컬럼 (컬럼 키: 순서 배열) */
    customOrderColumns?: Record<string, string[]>;
    /** 초기 정렬 설정 */
    initialSort?: SortConfig;
}

interface UseTableSortReturn<T> {
    /** 현재 정렬 설정 */
    sortConfig: SortConfig;
    /** 정렬된 데이터 */
    sortedData: T[];
    /** 정렬 핸들러 (컬럼 키 클릭 시 호출) */
    handleSort: (columnKey: string) => void;
    /** 정렬 초기화 */
    resetSort: () => void;
    /** 정렬 아이콘 렌더링 헬퍼 */
    getSortIconProps: (columnKey: string) => {
        isSorted: boolean;
        direction: SortDirection;
    };
}

/**
 * 테이블 정렬 훅 (키 기반)
 *
 * @example
 * ```tsx
 * const { sortedData, sortConfig, handleSort, getSortIconProps } = useTableSort({
 *     data: items,
 *     numericColumns: ['amount', 'balance'],
 *     dateColumns: ['createdAt', 'updatedAt']
 * });
 * ```
 */
export const useTableSort = <T extends object>({
    data,
    numericColumns = [],
    dateColumns = [],
    customOrderColumns = {},
    initialSort = { columnKey: null, direction: null }
}: UseTableSortOptions<T>): UseTableSortReturn<T> => {
    const [sortConfig, setSortConfig] = useState<SortConfig>(initialSort);

    /**
     * 정렬 핸들러
     * 동일한 컬럼 클릭: asc -> desc -> null 순환
     * 다른 컬럼 클릭: asc로 시작
     */
    const handleSort = useCallback((columnKey: string) => {
        setSortConfig((prev) => {
            if (prev.columnKey === columnKey) {
                if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
                if (prev.direction === 'desc') return { columnKey: null, direction: null };
            }
            return { columnKey, direction: 'asc' };
        });
    }, []);

    /**
     * 정렬 초기화
     */
    const resetSort = useCallback(() => {
        setSortConfig({ columnKey: null, direction: null });
    }, []);

    /**
     * 정렬 아이콘 props 반환
     */
    const getSortIconProps = useCallback((columnKey: string) => {
        const isSorted = sortConfig.columnKey === columnKey;
        return {
            isSorted,
            direction: isSorted ? sortConfig.direction : null
        };
    }, [sortConfig]);

    /**
     * 정렬된 데이터
     */
    const sortedData = useMemo(() => {
        if (!data || !sortConfig.columnKey || !sortConfig.direction) {
            return data || [];
        }

        const key = sortConfig.columnKey as keyof T;
        const isNumeric = (numericColumns as string[]).includes(sortConfig.columnKey);
        const isDate = (dateColumns as string[]).includes(sortConfig.columnKey);
        const customOrder = customOrderColumns[sortConfig.columnKey];

        return [...data].sort((a, b) => {
            const aVal = a[key];
            const bVal = b[key];

            // 커스텀 정렬 순서가 있는 경우
            if (customOrder) {
                const aStr = String(aVal || '');
                const bStr = String(bVal || '');

                const aIndex = customOrder.indexOf(aStr);
                const bIndex = customOrder.indexOf(bStr);

                // 둘 다 커스텀 순서에 있는 경우
                if (aIndex !== -1 && bIndex !== -1) {
                    return sortConfig.direction === 'asc'
                        ? aIndex - bIndex
                        : bIndex - aIndex;
                }

                // a만 커스텀 순서에 있는 경우 (a를 앞으로)
                if (aIndex !== -1) return sortConfig.direction === 'asc' ? -1 : 1;

                // b만 커스텀 순서에 있는 경우 (b를 앞으로)
                if (bIndex !== -1) return sortConfig.direction === 'asc' ? 1 : -1;

                // 둘 다 커스텀 순서에 없는 경우 문자열 비교
                return sortConfig.direction === 'asc'
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            }

            // 숫자 컬럼 처리
            if (isNumeric) {
                const aNum = aVal as number | null;
                const bNum = bVal as number | null;

                // null은 맨 뒤로
                if (aNum === null && bNum === null) return 0;
                if (aNum === null) return 1;
                if (bNum === null) return -1;

                // 숫자 비교 (0 포함)
                return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
            }

            // 날짜 컬럼 처리
            if (isDate) {
                const aDate = aVal as string | null;
                const bDate = bVal as string | null;

                // null/빈값은 맨 뒤로
                const aEmpty = aDate == null || aDate === '';
                const bEmpty = bDate == null || bDate === '';

                if (aEmpty && bEmpty) return 0;
                if (aEmpty) return 1;
                if (bEmpty) return -1;

                // 날짜 문자열 비교 (yyyy-MM-dd 형식)
                return sortConfig.direction === 'asc'
                    ? aDate.localeCompare(bDate)
                    : bDate.localeCompare(aDate);
            }

            // 일반 값 처리
            const aEmpty = aVal == null || aVal === '';
            const bEmpty = bVal == null || bVal === '';

            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return 1;
            if (bEmpty) return -1;

            // 타입에 따른 비교
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // 문자열 비교
            const aStr = String(aVal);
            const bStr = String(bVal);
            return sortConfig.direction === 'asc'
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });
    }, [data, sortConfig, numericColumns, dateColumns, customOrderColumns]);

    return {
        sortConfig,
        sortedData,
        handleSort,
        resetSort,
        getSortIconProps
    };
};

// ========================================
// 컬럼 인덱스 기반 정렬 훅
// ========================================
interface UseColumnSortOptions<T> {
    /** 정렬할 데이터 배열 */
    data: T[];
    /** 컬럼 인덱스에서 값을 추출하는 함수 */
    getValueByIndex: (item: T, columnIndex: number) => string | number;
    /** 초기 정렬 설정 */
    initialSort?: ColumnSortConfig;
}

interface UseColumnSortReturn<T> {
    /** 현재 정렬 설정 */
    sortConfig: ColumnSortConfig;
    /** 정렬된 데이터 */
    sortedData: T[];
    /** 정렬 핸들러 (컬럼 인덱스 클릭 시 호출) */
    handleSort: (columnIndex: number) => void;
    /** 정렬 초기화 */
    resetSort: () => void;
    /** 정렬 아이콘 렌더링 헬퍼 */
    getSortIconProps: (columnIndex: number) => {
        isSorted: boolean;
        direction: SortDirection;
    };
}

/**
 * 컬럼 인덱스 기반 테이블 정렬 훅
 * RiderSettlementHistory, RiderPreSettlementHistory 등에서 사용
 *
 * @example
 * ```tsx
 * const getValueByIndex = (item: Settlement, colIndex: number) => {
 *     switch (colIndex) {
 *         case 2: return item.riderId;
 *         case 3: return item.riderName;
 *         // ...
 *     }
 * };
 *
 * const { sortedData, sortConfig, handleSort } = useColumnSort({
 *     data: settlements,
 *     getValueByIndex
 * });
 * ```
 */
export const useColumnSort = <T>({
    data,
    getValueByIndex,
    initialSort = { columnIndex: null, direction: null }
}: UseColumnSortOptions<T>): UseColumnSortReturn<T> => {
    const [sortConfig, setSortConfig] = useState<ColumnSortConfig>(initialSort);

    /**
     * 정렬 핸들러
     */
    const handleSort = useCallback((columnIndex: number) => {
        setSortConfig((prev) => {
            if (prev.columnIndex === columnIndex) {
                if (prev.direction === 'asc') return { columnIndex, direction: 'desc' };
                if (prev.direction === 'desc') return { columnIndex: null, direction: null };
            }
            return { columnIndex, direction: 'asc' };
        });
    }, []);

    /**
     * 정렬 초기화
     */
    const resetSort = useCallback(() => {
        setSortConfig({ columnIndex: null, direction: null });
    }, []);

    /**
     * 정렬 아이콘 props 반환
     */
    const getSortIconProps = useCallback((columnIndex: number) => {
        const isSorted = sortConfig.columnIndex === columnIndex;
        return {
            isSorted,
            direction: isSorted ? sortConfig.direction : null
        };
    }, [sortConfig]);

    /**
     * 정렬된 데이터
     */
    const sortedData = useMemo(() => {
        if (!data || sortConfig.columnIndex === null || !sortConfig.direction) {
            return data || [];
        }

        return [...data].sort((a, b) => {
            const aVal = getValueByIndex(a, sortConfig.columnIndex!);
            const bVal = getValueByIndex(b, sortConfig.columnIndex!);

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortConfig.direction === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }

            return sortConfig.direction === 'asc'
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        });
    }, [data, sortConfig, getValueByIndex]);

    return {
        sortConfig,
        sortedData,
        handleSort,
        resetSort,
        getSortIconProps
    };
};
